import { mergeHead, type HeadDescriptor } from '../shared/seo/HeadDescriptor.js';
import { absolutizar } from '../shared/seo/absolutizar.js';
import { defaultHead } from '../shared/seo/defaultHead.js';
import { breadcrumbJsonLd, newsArticleJsonLd, type SiteInfo } from '../shared/seo/jsonld.js';
import type { NotaData } from './models/entities/NotaData.js';

/**
 * El `<head>` de una nota, en un solo lugar para los dos caminos que lo necesitan: el
 * loader lo serializa en el HTML del server (que es lo que ve el crawler) y la vista lo
 * reaplica con `useHead` al navegar de una nota a otra, donde no hay loader que corra.
 *
 * Función pura: el `SiteInfo` entra por parámetro (en el server sale de `ctx.site`, en el
 * cliente de `useSitio`), así que no depende de `window` ni del request.
 */
export function headDeNota(nota: NotaData['nota'], site: SiteInfo): HeadDescriptor {
  const urlAbsoluta = `${site.url}${nota.path}`;
  const categoria = nota.categories[0];
  // Absoluta a la fuerza: la URL que devuelve la API puede ser relativa, y un scraper de WhatsApp
  // o Facebook no la resuelve. Ver `absolutizar`.
  const imagen = absolutizar(site.url, nota.seo.ogImage?.url ?? nota.cover?.url);

  return mergeHead(defaultHead(site, nota.path), {
    title: nota.seo.metaTitle ?? `${nota.title} — ${site.name}`,
    description: nota.seo.metaDescription ?? nota.excerpt ?? undefined,
    canonical: nota.seo.canonicalUrl ?? urlAbsoluta,
    openGraph: {
      type: 'article',
      title: nota.seo.metaTitle ?? nota.title,
      image: imagen,
      imageAlt: nota.cover?.alt ?? undefined,
      publishedTime: nota.publishedAt,
      section: categoria?.name,
      authors: [nota.author],
    },
    jsonLd: [
      newsArticleJsonLd(
        {
          title: nota.title,
          description: nota.seo.metaDescription ?? nota.excerpt ?? undefined,
          url: urlAbsoluta,
          imageUrl: absolutizar(site.url, nota.cover?.url),
          publishedAt: nota.publishedAt,
          authorName: nota.author,
          section: categoria?.name,
        },
        site,
      ),
      breadcrumbJsonLd([
        { name: 'Portada', url: `${site.url}/` },
        ...(categoria ? [{ name: categoria.name, url: `${site.url}/${categoria.urlKey}` }] : []),
        { name: nota.title, url: urlAbsoluta },
      ]),
    ],
  });
}
